import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { Button, Box, Typography, Stack, Chip } from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import ConfirmationDialog from '@components/ConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import {
  viewMaintenanceSchedule,
  MAINTENANCE_SCHEDULE_NEW_ROUTE,
} from '../../legacy-payment-options.breadcrumbs';
import {
  getLegacyMaintenanceScheduleList,
  deleteLegacyMaintenanceSchedule,
  selectMaintenanceScheduleList,
  selectIsLoadingMaintenanceScheduleList,
} from '../../store/legacy-payment-options-maintenance-schedule.slice';

import {
  PAGE_DETAILS,
  MAINTENANCE_SCHEDULE_COLUMNS,
} from '../../legacy-payment-options.constants';

export function getMultiItemChipsCustomColumn(codes) {
  if (!codes) return null;

  return (
    <Stack
      flexDirection="row"
      gap={1}
      flexWrap="wrap">
      {codes.map((code) => (
        <Chip
          key={code}
          label={code}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function getMultilineCustomColumn(line1, line2) {
  return (
    <Box>
      <Typography
        variant="body1"
        fontWeight="500"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">{line2}</Typography>}
    </Box>
  );
}

function getCustomColumn(row, value, field) {
  switch (field) {
    case 'tabCodes':
    case 'pmids': {
      if (!value) {
        return '-';
      }

      return value.join(', ');
    }

    case 'startDateTime': {
      const date = moment(value).format('DD/MM/YYYY');
      const time = moment(value).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    case 'endDateTime': {
      const date = moment(value).format('DD/MM/YYYY');
      const time = moment(value).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    case 'createdBy': {
      const { createdBy, createdAt } = row;
      const date = moment(createdAt).format('DD/MM/YYYY, hh:mm A');
      return getMultilineCustomColumn(createdBy, date);
    }

    default:
      return value;
  }
}

function getListColumns() {
  return MAINTENANCE_SCHEDULE_COLUMNS.map(({ align, ...col }) => ({
    ...col,
    align,
    headerAlign: align,
    sortable: false,
    renderCell: ({ value, row }) => getCustomColumn(row, value, col.field),
  }));
}

function PropertyKeyToolbar({ enableDelete = false, onCreateNew, onDelete }) {
  return (
    <GridToolbarContainer sx={{ p: '12px', justifyContent: 'space-between' }}>
      <GridToolbarQuickFilter />

      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        {enableDelete && (
          <Button
            variant="contained"
            color="error"
            onClick={onDelete}
            startIcon={<DeleteIcon />}>
            Delete
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onCreateNew}
          startIcon={<AddIcon />}>
          Add new
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

export default function LegacyMaintenanceSchedule({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });

  const maintenanceScheduleList = useSelector(selectMaintenanceScheduleList);
  const isLoadingMaintenanceScheduleList = useSelector(
    selectIsLoadingMaintenanceScheduleList,
  );

  const enableDelete = selectedRows.length !== 0;

  // Authorization
  useEffect(() => {
    if (!permissions?.canViewLegacyMaintenanceSchedule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumb
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewMaintenanceSchedule));
  }, [dispatch]);

  // API: List
  useEffect(() => {
    dispatch(getLegacyMaintenanceScheduleList());
  }, [dispatch]);

  const onCreateNew = () => {
    navigate(MAINTENANCE_SCHEDULE_NEW_ROUTE);
  };

  const onDelete = () => {
    setShowDeleteModal({ show: true });
  };

  const onDeleteConfirmation = (items) => {
    dispatch(deleteLegacyMaintenanceSchedule(items)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage(PAGE_DETAILS.MaintenanceSchedule.DeleteSuccess),
        );

        dispatch(getLegacyMaintenanceScheduleList());
      } else {
        dispatch(
          showErrorMessage({
            message: PAGE_DETAILS.MaintenanceSchedule.DeleteFailure,
            duration: 5000,
          }),
        );
      }
    });
  };

  return (
    <ApplicationPageTemplate
      title={PAGE_DETAILS.MaintenanceSchedule.PageTitle}
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        density="comfortable"
        loading={isLoadingMaintenanceScheduleList}
        rows={maintenanceScheduleList}
        checkboxSelection={permissions?.canViewLegacyMaintenanceSchedule}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        disableColumnSelector
        disableDensitySelector
        onSelectionModelChange={(row) => {
          setSelectedRows(row);
        }}
        components={{
          Toolbar: () =>
            PropertyKeyToolbar({
              enableDelete,
              onCreateNew,
              onDelete,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        getRowId={({ id }) => id}
        columns={getListColumns()}
      />

      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={onDeleteConfirmation}
        data={selectedRows}
        title={PAGE_DETAILS.MaintenanceSchedule.DeleteConfirmationTitle}
        message={PAGE_DETAILS.MaintenanceSchedule.DeleteConfirmationMessage}
      />
    </ApplicationPageTemplate>
  );
}
